import { useState } from "react";
import { ChevronDown } from "lucide-react";
import { entityNameOf } from "../lib/findingCompat";

function fmt$(n) { return n ? `$${Number(n).toLocaleString("en-US", { maximumFractionDigits: 0 })}` : "—"; }
function fmtN(n) { return n != null ? Number(n).toLocaleString("en-US", { maximumFractionDigits: 1 }) : "—"; }

const STATUS = {
  ENABLED: "bg-emerald-100 text-emerald-700",
  PAUSED:  "bg-slate-100 text-slate-500",
  REMOVED: "bg-rose-100 text-rose-600",
};

const DEVICE_LABEL = {
  MOBILE:  "Mobile",
  DESKTOP: "Desktop",
  TABLET:  "Tablet",
  CONNECTED_TV: "CTV",
  OTHER:   "Other",
};

const AGE_LABEL = {
  AGE_RANGE_18_24: "18–24",
  AGE_RANGE_25_34: "25–34",
  AGE_RANGE_35_44: "35–44",
  AGE_RANGE_45_54: "45–54",
  AGE_RANGE_55_64: "55–64",
  AGE_RANGE_65_UP: "65+",
  AGE_RANGE_UNDETERMINED: "Unknown",
};

const GENDER_LABEL = {
  MALE: "Male",
  FEMALE: "Female",
  UNDETERMINED: "Unknown",
};

const SECTION_LABEL = {
  account_overview:         null,
  what_is_working:          { text: "Working",  cls: "text-emerald-700 bg-emerald-50 border-emerald-200" },
  worked_before_missing_now:{ text: "Worked Before", cls: "text-amber-700 bg-amber-50 border-amber-200" },
  active_but_not_working:   { text: "Draining",  cls: "text-rose-700 bg-rose-50 border-rose-200" },
  structural_gaps:          { text: "Structural", cls: "text-slate-600 bg-slate-50 border-slate-200" },
  new_campaign_opportunities:{ text: "Opportunity", cls: "text-blue-700 bg-blue-50 border-blue-200" },
  next_steps:               null,
};

function buildFindingsMap(sections) {
  const map = {};
  for (const section of (sections || [])) {
    const label = SECTION_LABEL[section.key];
    if (!label) continue;
    for (const f of (section.findings || [])) {
      const entityName = entityNameOf(f);
      if (!entityName) continue;
      const key = entityName.toLowerCase();
      if (!map[key]) map[key] = [];
      map[key].push({ ...f, _label: label });
    }
  }
  return map;
}

export default function CampaignView({ summary, sections }) {
  const campaigns = (summary?.campaigns_summary?.campaigns || []).filter(c => c.cost > 0);
  const adGroups  = summary?.ad_groups_summary?.by_campaign || {};
  const demo      = summary?.demographics_summary || {};
  const [expanded, setExpanded] = useState(null);
  const findingsMap = buildFindingsMap(sections);

  const device = demo.device || {};
  const age    = demo.age    || {};
  const gender = demo.gender || {};
  const hasDemo = Object.keys(device).length > 0;

  return (
    <div className="space-y-4">
      {/* Campaigns list */}
      <div className="card-soft overflow-hidden">
        <div className="px-6 pt-6 pb-3">
          <h3 className="font-heading text-xl font-medium text-slate-900">All Campaigns</h3>
          <p className="mt-1 text-xs text-slate-500">{campaigns.length} campaigns · sorted by spend</p>
        </div>

        <div className="divide-y divide-slate-100">
          {campaigns.map((c) => {
            const groups = adGroups[c.name] || [];
            const isOpen = expanded === c.campaign_id;
            const learnings = findingsMap[c.name.toLowerCase()] || [];
            const roas = c.roas ? `${c.roas.toFixed(2)}x` : "—";
            const cpa  = c.conversions > 0 ? fmt$(c.cpa) : "—";
            const bid  = (c.bidding_strategy_type || "").replace(/_/g, " ").replace(/\b\w/g, l => l.toUpperCase()) || "—";
            const tcpa = c.target_cpa_micros ? ` · tCPA ${fmt$(c.target_cpa_micros / 1e6)}` : "";
            const troas = c.target_roas ? ` · tROAS ${Number(c.target_roas).toFixed(2)}x` : "";

            return (
              <div key={c.campaign_id}>
                <button
                  onClick={() => setExpanded(isOpen ? null : c.campaign_id)}
                  className="w-full px-6 py-4 text-left hover:bg-slate-50 transition-colors"
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className={`inline-flex rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${STATUS[c.status] || STATUS.PAUSED}`}>
                          {c.status}
                        </span>
                        <span className="text-[10px] text-slate-400 uppercase tracking-wide">{c.channel_type}</span>
                        <span className="font-medium text-slate-900 text-sm truncate">{c.name}</span>
                      </div>
                      <div className="mt-2 flex flex-wrap gap-x-5 gap-y-1 text-xs text-slate-500">
                        <span>Spend <span className="font-semibold text-slate-800 num">{fmt$(c.cost)}</span></span>
                        <span>Conv <span className="font-semibold text-slate-800 num">{fmtN(c.conversions)}</span></span>
                        <span>ROAS <span className="font-semibold text-slate-800 num">{roas}</span></span>
                        <span>CPA <span className="font-semibold text-slate-800 num">{cpa}</span></span>
                        {c.budget_daily > 0 && <span>Budget <span className="font-semibold text-slate-800 num">{fmt$(c.budget_daily)}/day</span></span>}
                        {c.avg_impression_share != null && (
                          <span>IS <span className="font-semibold text-slate-800 num">{c.avg_impression_share}%</span></span>
                        )}
                      </div>
                      <p className="mt-1 text-[11px] text-slate-400">{bid}{tcpa}{troas}</p>
                    </div>
                    <ChevronDown
                      size={16}
                      strokeWidth={1.8}
                      className={`mt-1 flex-shrink-0 text-slate-400 transition-transform ${isOpen ? "rotate-180" : ""}`}
                    />
                  </div>
                </button>

                {isOpen && (groups.length > 0 || learnings.length > 0) && (
                  <div className="border-t border-slate-100">
                    {groups.length > 0 && (
                      <div className="bg-slate-50/50 px-6 py-3">
                        <p className="mb-2 text-[10px] font-semibold uppercase tracking-wider text-slate-400">Ad Groups</p>
                        <div className="space-y-0.5">
                          {groups.map((g) => {
                            const groas = g.cost > 0 ? `${g.roas.toFixed(2)}x` : "—";
                            const gcpa  = g.conversions > 0 ? fmt$(g.cpa) : "—";
                            const flag  = g.cost > 0 && g.conversions === 0;
                            return (
                              <div key={g.ad_group_id} className="flex items-center justify-between gap-4 rounded-md px-3 py-2 hover:bg-white text-xs">
                                <div className="flex items-center gap-2 min-w-0">
                                  <span className={`h-1.5 w-1.5 flex-shrink-0 rounded-full ${g.status === "ENABLED" ? "bg-emerald-400" : "bg-slate-300"}`} />
                                  <span className="truncate text-slate-800 font-medium">{g.name}</span>
                                  {flag && <span className="flex-shrink-0 rounded-full bg-rose-50 border border-rose-200 px-1.5 py-0.5 text-[10px] font-semibold text-rose-600">zero conv</span>}
                                </div>
                                <div className="flex-shrink-0 flex gap-4 text-slate-500 num">
                                  <span>{fmt$(g.cost)}</span>
                                  <span>{fmtN(g.conversions)} conv</span>
                                  <span>{groas} ROAS</span>
                                  <span>{gcpa} CPA</span>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                    {learnings.length > 0 && (
                      <div className="px-6 py-4 bg-white border-t border-slate-100">
                        <p className="mb-2.5 text-[10px] font-semibold uppercase tracking-wider text-slate-400">Key Learnings</p>
                        <div className="space-y-2.5">
                          {learnings.map((f, i) => (
                            <div key={i} className="flex items-start gap-2.5">
                              <span className={`mt-0.5 flex-shrink-0 rounded-full border px-2 py-0.5 text-[10px] font-semibold ${f._label.cls}`}>
                                {f._label.text}
                              </span>
                              <div className="min-w-0">
                                <p className="text-xs font-semibold text-slate-800 leading-snug">{f.title}</p>
                                <p className="text-xs text-slate-500 leading-relaxed">{f.detail}</p>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Demographics */}
      {hasDemo && (
        <div className="card-soft p-6">
          <h3 className="font-heading text-xl font-medium text-slate-900 mb-5">Demographics</h3>
          <div className="grid gap-6 sm:grid-cols-3">
            <DemoBlock title="Device" data={device} labelMap={DEVICE_LABEL} />
            <DemoBlock title="Age Range" data={age} labelMap={AGE_LABEL} />
            <DemoBlock title="Gender" data={gender} labelMap={GENDER_LABEL} />
          </div>
        </div>
      )}
    </div>
  );
}

function DemoBlock({ title, data, labelMap }) {
  if (!Object.keys(data).length) return null;
  const total = Object.values(data).reduce((s, d) => s + d.cost, 0);
  return (
    <div>
      <p className="mb-3 text-[10px] font-semibold uppercase tracking-wider text-slate-400">{title}</p>
      <div className="space-y-2">
        {Object.entries(data).map(([key, d]) => {
          const pct = total > 0 ? (d.cost / total) * 100 : 0;
          const roas = d.roas ? `${d.roas.toFixed(2)}x` : "—";
          const label = labelMap[key] || key.replace(/_/g, " ");
          return (
            <div key={key}>
              <div className="flex items-center justify-between mb-1">
                <span className="text-xs font-medium text-slate-700">{label}</span>
                <span className="text-xs text-slate-500 num">{roas} ROAS · {pct.toFixed(0)}%</span>
              </div>
              <div className="h-1.5 w-full rounded-full bg-slate-100 overflow-hidden">
                <div
                  className="h-full rounded-full bg-[#0052FF]"
                  style={{ width: `${pct}%`, opacity: d.conversions > 0 ? 1 : 0.35 }}
                />
              </div>
              <p className="mt-0.5 text-[10px] text-slate-400 num">{fmt$(d.cost)} · {fmtN(d.conversions)} conv</p>
            </div>
          );
        })}
      </div>
    </div>
  );
}
